// "Sign in to finish your deck (2 free)": getting a guest back to the SAME
// deck after sign-in.
//
// A guest's deck lives in localStorage under a local id. Sign-in returns to
// the same URL (/decks/<local id>/edit), but a signed-in list hides local
// decks until the user brings them over (useSavedDecks.migrateLocalDecks),
// which gives each one a NEW cloud id. So:
//  - the Finish button remembers the local id before leaving (this tab's
//    sessionStorage, an hour at most);
//  - a migration records local id -> cloud id here;
//  - DecksPage, on a remembered id it cannot find, offers the migration and
//    then opens the editor on the migrated id.
import { FINISH_RETURN_KEY, MIGRATED_IDS_KEY } from './finishDeck.codes'

const MAX_AGE_MS = 60 * 60 * 1000

function store(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

export function rememberFinishReturn(deckId: string, now: number = Date.now()): void {
  try {
    store()?.setItem(FINISH_RETURN_KEY, JSON.stringify({ deckId, at: now }))
  } catch {
    // storage refused: sign-in still works, the return is best effort
  }
}

/** The remembered deck id, or null (none, malformed, or over an hour old). */
export function readFinishReturn(now: number = Date.now()): string | null {
  try {
    const raw = store()?.getItem(FINISH_RETURN_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as { deckId?: unknown; at?: unknown }
    if (typeof v.deckId !== 'string' || !v.deckId || typeof v.at !== 'number') return null
    if (now - v.at > MAX_AGE_MS || now < v.at) return null
    return v.deckId
  } catch {
    return null
  }
}

export function clearFinishReturn(): void {
  try {
    store()?.removeItem(FINISH_RETURN_KEY)
  } catch {
    // nothing to clear
  }
}

/** Records a migration's local id -> cloud id pairs (merged over earlier ones). */
export function recordMigratedIds(map: Record<string, string>): void {
  try {
    const s = store()
    if (!s) return
    const prev = JSON.parse(s.getItem(MIGRATED_IDS_KEY) ?? '{}') as Record<string, string>
    s.setItem(MIGRATED_IDS_KEY, JSON.stringify({ ...(prev && typeof prev === 'object' ? prev : {}), ...map }))
  } catch {
    // best effort
  }
}

/** The cloud id a local deck became, or null. */
export function migratedIdFor(localId: string): string | null {
  try {
    const raw = store()?.getItem(MIGRATED_IDS_KEY)
    if (!raw) return null
    const map = JSON.parse(raw) as Record<string, unknown>
    const id = map?.[localId]
    return typeof id === 'string' && id ? id : null
  } catch {
    return null
  }
}
